import React, {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {MultiPolygon} from "geojson";
import {useAtomValue} from "jotai";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {Alert, Button, Grid, Stack, Typography} from "@mui/material";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import AddIcon from "@mui/icons-material/AddOutlined";
import {Dialog} from "../base/Dialog";
import {useFieldManagement} from "../../hooks/fieldManagementHooks";
import {Frame} from "../base/Frame";
import {Field, FieldPostBody} from "../../types/domain/field";
import {swallowException} from "../../utils/appUtils";
import {getResourceErrorMessage, isResourceFailed} from "../../utils/resourceUtils";
import {AutocompleteInput} from "../base/AutocompleteInput";
import {TextInput} from "../base/TextInput";
import {Order} from "../../types/domain/order";
import {authenticatedUserLoadable} from "../../store/authenticatedUserStore";
import {isUserHaveAnyOfGlobalRoles} from "../../utils/accessControlUtils";
import {UserGlobalRole} from "../../types/domain/user";
import {toCropCyclePost} from "../../utils/cropCycleUtils";
import {CheckboxInput} from "../base/CheckboxInput";
import {FarmCreateDialog} from "../dialogs/FarmCreateDialog";
import {useFarms} from "../../hooks/farmsHooks";
import {useOrders} from "../../hooks/ordersHooks";
import {LabeledInput} from "../base/LabeledInput";
import {TextFieldFramed} from "../base/TextFieldFramed";
import {CropWidget} from "../fields/CropWidget";
import {FieldCreateFormInputType, getFieldCreateSchema} from "../../utils/fieldValidationUtils";
import {constructValues, convertToHasIdWithName, getDefaultForNewCropCycle} from "../../utils/fieldCreateUtils";
import {SectionHeader} from "../base/SectionHeader";
import {useSoils} from "../../hooks/soilsHooks";
import {useIrrigations} from "../../hooks/irrigationsHooks";
import {useCrops} from "../../hooks/cropsHooks";
import {FieldStatus} from "../../types/domain/fieldStatus";
import {InfoMessage} from "../base/InfoMessage";
import {useResourceValueOrDefault} from "../../hooks/resourceHooks";
import {getIrrigationDefaultValue, IrrigationDataKeys} from "../../utils/irrigationDefaults";

export interface Props {
    open: boolean;
    onClose: (isSuccessful: boolean) => void;
    companyId: string;
    selectedField: Field;
}

const OrderInfo: React.FC<{order: Order; isAdmin: boolean}> = ({order, isAdmin}) => {
    const {t} = useTranslation();
    return (
        <Frame>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <SectionHeader label={t("dialogs.field_create.form.order_section_title")} />
                </Grid>

                <Grid item xs={6} sx={!isAdmin ? visuallyHidden : {}}>
                    <LabeledInput label={t("orders.order_create.subscription_type")}>
                        <TextFieldFramed value={order.subscriptionType}></TextFieldFramed>
                    </LabeledInput>
                </Grid>
                <Grid item xs={isAdmin ? 6 : 12}>
                    <LabeledInput label={t("orders.order_create.order_nb")}>
                        <TextFieldFramed value={`${order.id}`}></TextFieldFramed>
                    </LabeledInput>
                </Grid>

                <Grid item xs={6}>
                    <LabeledInput label={t("orders.order_create.subscription_start")}>
                        <TextFieldFramed value={order.startDate ? order.startDate : ""}></TextFieldFramed>
                    </LabeledInput>
                </Grid>
                <Grid item xs={6}>
                    <LabeledInput label={t("orders.order_create.subscription_end")}>
                        <TextFieldFramed value={order.endDate ? order.endDate : ""}></TextFieldFramed>
                    </LabeledInput>
                </Grid>
            </Grid>
        </Frame>
    );
};

export const FieldEditDialog: React.FC<Props> = ({open, onClose, companyId, selectedField}) => {
    const {t} = useTranslation();
    const [currentField, setCurrentField] = useState(selectedField);
    const [order, setOrder] = useState<Order | undefined>();
    const [farm, setFarm] = useState<{id: string} | undefined>();
    const [farmDialogOpened, setFarmDialogOpened] = useState<boolean>(false);

    const onFarmDialogClose = () => {
        setFarmDialogOpened(false);
    };

    const authenticatedUserResource = useAtomValue(authenticatedUserLoadable);
    const authenticatedUser = useResourceValueOrDefault(authenticatedUserResource, undefined);
    const isAdmin =
        authenticatedUser !== undefined &&
        isUserHaveAnyOfGlobalRoles(authenticatedUser, [UserGlobalRole.ADMIN, UserGlobalRole.CS]);

    const fieldManagement = useFieldManagement();
    const {soils} = useSoils();
    const {irrigations} = useIrrigations();
    const {crops} = useCrops();

    const [geometry] = useState<MultiPolygon | null>(currentField ? currentField.geometry : null);

    const farmsState = useFarms(companyId);
    const ordersState = useOrders(companyId);
    const allFarms = farmsState.items;
    const showFieldEditInfoMessage = useMemo(() => {
        return selectedField.status === FieldStatus.FINAL && !isAdmin;
    }, [selectedField, isAdmin]);

    useEffect(() => {
        if (ordersState.items.length > 0) {
            const order = ordersState.items.find((item) => item.id === currentField.orderId);
            setOrder(order);
        }
    }, [ordersState.items, companyId, currentField]);

    const initFormValues = useMemo(() => {
        return constructValues(currentField);
    }, [currentField]);

    const {
        control,
        handleSubmit,
        register,
        reset,
        setValue,
        formState: {errors},
        watch,
    } = useForm<FieldCreateFormInputType>({
        defaultValues: initFormValues,
        resolver: zodResolver(getFieldCreateSchema(t)),
    });

    //If a new farm was created, we set in control, via setValue method of useForm hook
    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        farmsState.refresh();
        setValue("farmId", farm ? farm.id : null);
    }, [farm, setValue]);

    const constructFieldPostBody = (inputs: FieldCreateFormInputType): FieldPostBody => {
        const priority = inputs.priority === true ? 1 : 0;
        const field: FieldPostBody = {
            soilId: inputs.soilId,
            irrigationTypeId: inputs.irrigationTypeId,
            name: inputs.name,
            priority: priority,
            irrigationInterval: inputs.irrigationInterval,
            desiredMoistureLevel: inputs.desiredMoistureLevel,
            irrigationCriteria: inputs.irrigationCriteria,
            irrigationDefault: inputs.irrigationDefault,
            satSoilMoistBias: inputs.satSoilMoistBias,
            KSATBias: inputs.KSATBias,
            farmId: inputs.farmId,
            orderId: inputs.orderId,
            //Values not coming from inputs.
            geometry: null,
            cropCycles: inputs.cropCycles.map((item) => toCropCyclePost(item)),
            onFarmEfficiencyMin: inputs.onFarmEfficiencyMin,
            minAppliedWater: inputs.minAppliedWater,
            maxAppliedWater: inputs.maxAppliedWater,
        };
        field.geometry = geometry;
        return field;
    };

    useEffect(() => {
        reset(initFormValues);
    }, [initFormValues, reset]);

    const onSubmit = async (data: FieldCreateFormInputType) => {
        const fieldBody = constructFieldPostBody(data);
        await fieldManagement.edit(companyId, currentField.id, fieldBody);
        onClose(true);
    };

    const onDelete = () => {
        fieldManagement.reset();
        if (currentField?.id) {
            fieldManagement
                .delete(companyId, currentField.id)
                .then(() => {
                    onClose(true);
                })
                .catch(swallowException);
        } else {
            onClose(true);
        }
    };

    const handleAddNewCropCycle = () => {
        const updatedCropCycles = [...currentField.cropCycles];
        const newCropCycle = getDefaultForNewCropCycle();
        //we put new value as the 0th item in the list
        updatedCropCycles.unshift(newCropCycle);
        const updatedField = {...currentField, cropCycles: updatedCropCycles};
        setCurrentField(updatedField);
    };
    const renderStepTwo = () => {
        const watchedIrrTypeId = watch("irrigationTypeId");
        const calculatedValue = (value: IrrigationDataKeys) => {
            return watchedIrrTypeId !== "0" ? getIrrigationDefaultValue(parseInt(watchedIrrTypeId), value) : "";
        };

        return (
            <>
                <form>
                    {isResourceFailed(fieldManagement.resource) && (
                        <Alert severity="error">{getResourceErrorMessage(fieldManagement.resource)}</Alert>
                    )}
                    <Grid container spacing={2}>
                        {showFieldEditInfoMessage && (
                            <Grid item xs={12}>
                                <InfoMessage message={t("dialogs.field_create.edit_info_message")} />
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <LabeledInput label={t("dialogs.field_create.form.field_name")}>
                                <TextInput name="name" type="text" register={register} errors={errors} />
                            </LabeledInput>
                        </Grid>
                        <Grid item xs={12}>
                            <Stack direction="column">
                                <Typography fontSize={"0.75rem"} textTransform="uppercase" color={"#737990"}>
                                    {t("dialogs.field_create.form.farm")}
                                </Typography>
                                <Stack direction="row" gap="1rem" alignItems="center">
                                    <AutocompleteInput
                                        name="farmId"
                                        control={control}
                                        options={allFarms}
                                        noOptionsText={t("dialogs.field_create.form.no_farm_options")}
                                        disableClear
                                        initialValue={initFormValues.farmId}
                                        getOptionLabel={(option) => option.name}
                                    />
                                    <Button
                                        color="primary"
                                        startIcon={<AddIcon />}
                                        onClick={() => {
                                            setFarmDialogOpened(true);
                                        }}
                                        sx={{
                                            flexGrow: 0,
                                        }}
                                    >
                                        {t("dialogs.field_create.form.new_button")}
                                    </Button>
                                </Stack>
                            </Stack>
                        </Grid>
                        <>
                            <CropWidget
                                mode="edit"
                                t={t}
                                crops={crops}
                                control={control}
                                register={register}
                                errors={errors}
                                handleAddNewCropCycle={handleAddNewCropCycle}
                                initFormValues={initFormValues}
                                isAdmin={isAdmin}
                            />
                        </>
                        <Grid item xs={12}>
                            <Frame>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <SectionHeader
                                            label={t("dialogs.field_create.form.irrigation_section_title")}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <LabeledInput label={t("dialogs.field_create.form.irrigation_type")}>
                                            <AutocompleteInput
                                                name="irrigationTypeId"
                                                control={control}
                                                options={irrigations}
                                                disableClear
                                                initialValue={initFormValues.irrigationTypeId}
                                                getOptionLabel={(option) => option.name}
                                                placeholder={t("dialogs.field_create.form.automcomplete_placeholder")}
                                            />
                                        </LabeledInput>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <LabeledInput label={t("dialogs.field_create.form.irrigation_interval")}>
                                            <AutocompleteInput
                                                name="irrigationInterval"
                                                control={control}
                                                options={convertToHasIdWithName([1, 2, 3, 4, 5, 6, 7, 8])}
                                                initialValue={initFormValues.irrigationInterval}
                                                getOptionLabel={(option) => option.name}
                                                placeholder={calculatedValue("irrigation_rotation_default") as string}
                                            />
                                        </LabeledInput>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <LabeledInput label={t("dialogs.field_create.form.moisture_level")}>
                                            <TextInput
                                                name="desiredMoistureLevel"
                                                type="number"
                                                register={register}
                                                errors={errors}
                                                placeholder={calculatedValue("desirable_storage") as string}
                                            />
                                        </LabeledInput>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <LabeledInput label={t("dialogs.field_create.form.irrigation_criteria")}>
                                            <TextInput
                                                name="irrigationCriteria"
                                                type="number"
                                                register={register}
                                                errors={errors}
                                                placeholder={calculatedValue("irrigation_criterium_min") as string}
                                            />
                                        </LabeledInput>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <LabeledInput label={t("dialogs.field_create.form.irrigation_default")}>
                                            <TextInput
                                                name="irrigationDefault"
                                                type="number"
                                                register={register}
                                                errors={errors}
                                                placeholder={calculatedValue("irrigation_default") as string}
                                            />
                                        </LabeledInput>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <LabeledInput label={t("dialogs.field_create.form.on_farm_efficiency_min")}>
                                            <TextInput
                                                name="onFarmEfficiencyMin"
                                                type="number"
                                                register={register}
                                                errors={errors}
                                                placeholder={calculatedValue("on_farm_efficiency_min") as string}
                                            />
                                        </LabeledInput>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <LabeledInput label={t("dialogs.field_create.form.min_applied_water")}>
                                            <TextInput
                                                name="minAppliedWater"
                                                type="number"
                                                register={register}
                                                errors={errors}
                                                placeholder={calculatedValue("min_applied_water") as string}
                                            />
                                        </LabeledInput>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <LabeledInput label={t("dialogs.field_create.form.max_applied_water")}>
                                            <TextInput
                                                name="maxAppliedWater"
                                                type="number"
                                                register={register}
                                                errors={errors}
                                                placeholder={calculatedValue("max_applied_water") as string}
                                            />
                                        </LabeledInput>
                                    </Grid>
                                </Grid>
                            </Frame>
                        </Grid>

                        <Grid item xs={12}>
                            <Frame>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <SectionHeader label={t("dialogs.field_create.form.soil_section_title")} />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <LabeledInput label={t("dialogs.field_create.form.soil_type")}>
                                            <AutocompleteInput
                                                name="soilId"
                                                control={control}
                                                options={soils}
                                                disableClear
                                                initialValue={initFormValues.soilId}
                                                getOptionLabel={(option) => option.name}
                                                placeholder={t("dialogs.field_create.form.automcomplete_placeholder")}
                                            />
                                        </LabeledInput>
                                    </Grid>
                                    <Grid item xs={6} sx={!isAdmin ? visuallyHidden : {}}>
                                        <LabeledInput label={t("dialogs.field_create.form.sat_soil_moisture_bias")}>
                                            <TextInput
                                                name="satSoilMoistBias"
                                                type="number"
                                                register={register}
                                                errors={errors}
                                            />
                                        </LabeledInput>
                                    </Grid>

                                    <Grid item xs={6} sx={!isAdmin ? visuallyHidden : {}}>
                                        <LabeledInput label={t("dialogs.field_create.form.ksat_bias")}>
                                            <TextInput
                                                name="KSATBias"
                                                type="number"
                                                register={register}
                                                errors={errors}
                                            />
                                        </LabeledInput>
                                    </Grid>
                                </Grid>
                            </Frame>
                        </Grid>
                        {order && (
                            <Grid item xs={12}>
                                <OrderInfo order={order} isAdmin={isAdmin} />
                            </Grid>
                        )}
                        {isAdmin && (
                            <Grid item xs={12}>
                                <Frame>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <SectionHeader
                                                label={t("dialogs.field_create.form.extra_section_title")}
                                            ></SectionHeader>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <CheckboxInput
                                                name="priority"
                                                control={control}
                                                initialValue={initFormValues.priority}
                                                label={t("dialogs.field_create.form.priority")}
                                                inputProps={{sx: {marginTop: "0.25rem"}}}
                                            />
                                        </Grid>
                                    </Grid>
                                </Frame>
                            </Grid>
                        )}
                    </Grid>
                </form>
                {farmDialogOpened && companyId && (
                    <FarmCreateDialog
                        mode="new"
                        companyId={companyId}
                        open={farmDialogOpened}
                        onClose={onFarmDialogClose}
                        setFarm={setFarm}
                    />
                )}
            </>
        );
    };
    return (
        <>
            <Dialog
                enableSubmit={true}
                enableDelete={true}
                deleteTitle={t("dialogs.actions.delete")}
                submitTitle={t("dialogs.actions.submit")}
                title={t("dialogs.field_create.edit_title")}
                titleIcon={<FmdGoodOutlinedIcon color="primary" />}
                open={open}
                deleteConfirmation={t("dialogs.field_create.delete_confirmation")}
                disableDefaultClose={true}
                onClose={() => onClose(false)}
                onDelete={onDelete}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onSubmit={handleSubmit(onSubmit)}
            >
                <>{renderStepTwo()}</>
            </Dialog>
        </>
    );
};

const visuallyHidden = {
    position: "absolute",
    height: "1px",
    width: "1px",
    overflow: "hidden",
    whiteSpace: "nowrap",
};
